import type { User } from "firebase/auth";
import {
  createContext,
  useEffect,
  useState,
  type ReactNode,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getDealerPortalV1Me,
  ApiError,
  type DtoMeResponseWithFeatureFlags,
  type DtoMeResponse,
} from "@porchpass/api-client";
import { useApiCall } from "@porchpass/ui";
import { useAuth } from "./AuthContext";
import {
  FeatureFlagsProvider,
  defaultFeatureFlags,
  type FeatureFlags,
} from "./FeatureFlagsContext";

export const DealersContext = createContext<{
  dealerOptions: DtoMeResponse[];
  isLoading: boolean;
} | null>(null);

export const DealersProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [previousUser, setPreviousUser] = useState<User | null>(null);
  const [dealerOptions, setDealerOptions] = useState<DtoMeResponse[]>([]);
  const [featureFlags, setFeatureFlags] =
    useState<FeatureFlags>(defaultFeatureFlags);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useApiCall(
    async () => {
      const resp = await getDealerPortalV1Me();
      return resp.data as DtoMeResponseWithFeatureFlags;
    },
    {
      toast: {
        error: "Unable to load locations. Please try again or contact support.",
      },
      onSuccess: (data) => {
        setDealerOptions(data.me_responses ?? []);
        setFeatureFlags({
          isDealerGeneratedAppEnabled:
            data.is_dealer_generated_app_enabled ?? false,
        });
        setIsLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching dealer options", error);
        if (
          error instanceof ApiError &&
          (error.status === 403 || error.status === 404)
        ) {
          void navigate("/error", {
            state: { status: error.status },
            replace: true,
          });
        }
        setIsLoading(false);
      },
    },
  );

  useEffect(() => {
    if (user) {
      if (!previousUser || previousUser.uid !== user.uid) {
        setIsLoading(true);
        void fetchMe.execute();
      }
      setPreviousUser(user);
    }
  }, [user]);

  return (
    <DealersContext.Provider value={{ dealerOptions, isLoading }}>
      <FeatureFlagsProvider featureFlags={featureFlags}>
        {children}
      </FeatureFlagsProvider>
    </DealersContext.Provider>
  );
};

export const useDealers = () => {
  const context = useContext(DealersContext);
  if (!context) {
    throw new Error("useDealers must be used within a DealersProvider");
  }
  return context;
};
